import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [currentCategory, setCurrentCategory] = useState(slug);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    setCurrentCategory(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const filteredComics = currentCategory === 'all'
    ? comics
    : comics.filter((c) => c.category === currentCategory);

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'latest') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    if (sortBy === 'popular') return b.views - a.views;
    return b.rating - a.rating;
  });

  const currentCategoryName = categories.find((c) => c.slug === currentCategory)?.name || '全部漫画';
  const canonicalSlug = currentCategory === 'all' ? '' : currentCategory;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategoryName} - 皮皮漫画网`,
    description: `皮皮漫画网${currentCategoryName}分类，提供丰富韩漫在线阅读资源，免费韩国漫画下拉式阅读。`,
    url: `https://mousehole.cn/category/${canonicalSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '皮皮漫画网',
      url: 'https://mousehole.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <main>
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-2">
              {currentCategoryName}
            </h1>
            <p className="text-sm text-foreground-600">
              发现精彩的{currentCategoryName}漫画，皮皮漫画网为您提供免费韩国漫画下拉式阅读体验
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Category Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-2 mb-6 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  currentCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-100 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Sort Options */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
            <div className="flex gap-1 bg-background-100 rounded-lg p-1">
              {([
                { key: 'latest', label: '最新更新' },
                { key: 'popular', label: '最受欢迎' },
                { key: 'rating', label: '评分最高' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="ml-auto text-xs text-foreground-400 whitespace-nowrap">
              共 {sortedComics.length} 部作品
            </div>
          </div>

          {/* Comics Grid */}
          {sortedComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-10">
              {sortedComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-book-2-line text-3xl text-secondary-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无漫画，请浏览其他分类</p>
              <Link to="/category/all" className="inline-block mt-4 px-6 py-2 rounded-lg bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}